"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { ChevronLeft, XIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { bringToastLayerToFront } from "@/components/toast-layer";

/**
 * Dialog primitive built on the native HTML `<dialog>` element (`showModal()`).
 *
 * Native modal dialogs render in the browser *top layer*, which sits above all
 * z-index content and orders itself by open-order — so no z-index juggling is
 * needed and stacked modals "just work" (Esc / backdrop / back button pop only
 * the topmost). Radix overlays (dropdown/select/popover/tooltip) opened inside a
 * dialog must portal into it via {@link useDialogContainer} to share the top
 * layer, otherwise they'd render behind the modal.
 *
 * The exported API intentionally mirrors the previous Radix-based one so call
 * sites don't change: controlled (`open`/`onOpenChange`) and uncontrolled
 * (`DialogTrigger`) modes, `asChild` on trigger/close, and `showCloseButton`.
 */

// --- shared open-state context -------------------------------------------------

type DialogContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
};
const DialogContext = React.createContext<DialogContextValue | null>(null);
function useDialogContext(component) {
  const ctx = React.useContext(DialogContext);
  if (!ctx) {
    throw new Error(`${component} must be used within <Dialog>`);
  }
  return ctx;
}

// --- container context (portal target for nested Radix overlays) ---------------

const DialogContainerContext = React.createContext<HTMLElement | null>(null);

/**
 * Returns the nearest open `<dialog>` element, for use as a Radix `Portal`
 * `container` so overlays opened inside a modal render *inside* its top-layer
 * element (and therefore above it). Returns `undefined` when not inside a
 * dialog, so Radix falls back to `document.body`.
 */
export function useDialogContainer() {
  return React.useContext(DialogContainerContext) ?? undefined;
}

// --- body scroll lock (shared across stacked dialogs) --------------------------

let openDialogCount = 0;
function lockBodyScroll() {
  if (typeof document === "undefined") return;
  if (openDialogCount === 0) {
    document.body.style.overflow = "hidden";
  }
  openDialogCount += 1;
}
function unlockBodyScroll() {
  if (typeof document === "undefined") return;
  openDialogCount = Math.max(0, openDialogCount - 1);
  if (openDialogCount === 0) {
    document.body.style.overflow = "";
  }
}

// --- root ----------------------------------------------------------------------

function Dialog(t0) {
  const $ = _c(9);
  const {
    open: openProp,
    defaultOpen,
    onOpenChange,
    children
  } = t0;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;
  let t1;
  if ($[0] !== isControlled || $[1] !== onOpenChange) {
    t1 = next => {
      if (!isControlled) {
        setUncontrolledOpen(next);
      }
      onOpenChange?.(next);
    };
    $[0] = isControlled;
    $[1] = onOpenChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const setOpen = t1;
  let t2;
  if ($[3] !== open || $[4] !== setOpen) {
    t2 = {
      open,
      setOpen
    };
    $[3] = open;
    $[4] = setOpen;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const value = t2;
  let t3;
  if ($[6] !== children || $[7] !== value) {
    t3 = <DialogContext.Provider value={value}>{children}</DialogContext.Provider>;
    $[6] = children;
    $[7] = value;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

// --- trigger / close -----------------------------------------------------------

function DialogTrigger(t0) {
  const $ = _c(11);
  let asChild;
  let onClick;
  let props;
  if ($[0] !== t0) {
    ({
      asChild,
      onClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = asChild;
    $[2] = onClick;
    $[3] = props;
  } else {
    asChild = $[1];
    onClick = $[2];
    props = $[3];
  }
  const ctx = useDialogContext("DialogTrigger");
  const Comp = asChild ? Slot : "button";
  let t1;
  if ($[4] !== ctx || $[5] !== onClick) {
    t1 = e => {
      onClick?.(e);
      ctx.setOpen(true);
    };
    $[4] = ctx;
    $[5] = onClick;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== Comp || $[8] !== props || $[9] !== t1) {
    t2 = <Comp data-slot="dialog-trigger" onClick={t1} {...props} />;
    $[7] = Comp;
    $[8] = props;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
function DialogClose(t0) {
  const $ = _c(11);
  let asChild;
  let onClick;
  let props;
  if ($[0] !== t0) {
    ({
      asChild,
      onClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = asChild;
    $[2] = onClick;
    $[3] = props;
  } else {
    asChild = $[1];
    onClick = $[2];
    props = $[3];
  }
  const ctx = useDialogContext("DialogClose");
  const Comp = asChild ? Slot : "button";
  let t1;
  if ($[4] !== ctx || $[5] !== onClick) {
    t1 = e => {
      onClick?.(e);
      ctx.setOpen(false);
    };
    $[4] = ctx;
    $[5] = onClick;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== Comp || $[8] !== props || $[9] !== t1) {
    t2 = <Comp data-slot="dialog-close" onClick={t1} {...props} />;
    $[7] = Comp;
    $[8] = props;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}

// Compatibility shims — the overlay is now the `::backdrop` pseudo-element and
// the `<dialog>` needs no portal, but these are kept exported for API parity.
function DialogPortal(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <>{children}</>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function DialogOverlay(_props: React.ComponentProps<"div">): null {
  void _props;
  return null;
}

// --- content -------------------------------------------------------------------

type DialogContentProps = Omit<React.ComponentProps<"dialog">, "onCancel"> & {
  showCloseButton?: boolean;
  /** Called when the user presses Esc; call `preventDefault()` to keep open. */
  onEscapeKeyDown?: (event: {
    preventDefault: () => void;
  }) => void;
  /** Called on backdrop click; call `preventDefault()` to keep open. */
  onInteractOutside?: (event: {
    preventDefault: () => void;
  }) => void;
};
function DialogContent(t0) {
  const $ = _c(38);
  let children;
  let className;
  let onClick;
  let onEscapeKeyDown;
  let onInteractOutside;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      children,
      showCloseButton: t1,
      onEscapeKeyDown,
      onInteractOutside,
      onClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onClick;
    $[4] = onEscapeKeyDown;
    $[5] = onInteractOutside;
    $[6] = props;
    $[7] = t1;
  } else {
    children = $[1];
    className = $[2];
    onClick = $[3];
    onEscapeKeyDown = $[4];
    onInteractOutside = $[5];
    props = $[6];
    t1 = $[7];
  }
  const showCloseButton = t1 === undefined ? true : t1;
  const ctx = useDialogContext("DialogContent");
  const t = useTranslations();
  const [dialogEl, setDialogEl] = React.useState(null);
  const wasOpenRef = React.useRef(false);
  let t2;
  let t3;
  if ($[8] !== ctx.open || $[9] !== dialogEl) {
    t2 = () => {
      const el = dialogEl;
      if (!el) {
        return;
      }
      if (ctx.open && !el.open) {
        el.showModal();
        wasOpenRef.current = true;
        lockBodyScroll();
        bringToastLayerToFront();
      } else {
        if (!ctx.open && el.open) {
          el.close();
          if (wasOpenRef.current) {
            wasOpenRef.current = false;
            unlockBodyScroll();
          }
        }
      }
    };
    t3 = [ctx.open, dialogEl];
    $[8] = ctx.open;
    $[9] = dialogEl;
    $[10] = t2;
    $[11] = t3;
  } else {
    t2 = $[10];
    t3 = $[11];
  }
  React.useEffect(t2, t3);
  let t4;
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => () => {
      if (wasOpenRef.current) {
        wasOpenRef.current = false;
        unlockBodyScroll();
      }
    };
    t5 = [];
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  React.useEffect(t4, t5);
  let t6;
  if ($[14] !== ctx) {
    t6 = callback => {
      let prevented = false;
      callback?.({
        preventDefault: () => prevented = true
      });
      if (!prevented) {
        ctx.setOpen(false);
      }
    };
    $[14] = ctx;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const requestClose = t6;
  let t7;
  if ($[16] !== onEscapeKeyDown || $[17] !== requestClose) {
    t7 = e => {
      e.preventDefault();
      requestClose(onEscapeKeyDown);
    };
    $[16] = onEscapeKeyDown;
    $[17] = requestClose;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const handleCancel = t7;
  let t8;
  if ($[19] !== dialogEl || $[20] !== onClick || $[21] !== onInteractOutside || $[22] !== requestClose) {
    t8 = e_0 => {
      onClick?.(e_0);
      if (e_0.target !== dialogEl) {
        return;
      }
      const rect = dialogEl.getBoundingClientRect();
      const inside = rect.top <= e_0.clientY && e_0.clientY <= rect.top + rect.height && rect.left <= e_0.clientX && e_0.clientX <= rect.left + rect.width;
      if (!inside) {
        requestClose(onInteractOutside);
      }
    };
    $[19] = dialogEl;
    $[20] = onClick;
    $[21] = onInteractOutside;
    $[22] = requestClose;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const handleClick = t8;
  let t9;
  if ($[24] !== className) {
    t9 = cn("ha-dialog grid content-start gap-4", "m-auto w-full max-w-[calc(100%-2rem)] max-h-[calc(100%-2rem)] overflow-y-auto", "rounded-lg border bg-background p-6 text-foreground shadow-lg outline-none", "md:max-w-2xl", className);
    $[24] = className;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== children || $[27] !== ctx || $[28] !== dialogEl || $[29] !== showCloseButton || $[30] !== t) {
    t10 = ctx.open && <DialogContainerContext.Provider value={dialogEl}>{showCloseButton && <button type="button" data-slot="dialog-back" onClick={() => ctx.setOpen(false)} className="sticky -top-6 z-10 -mx-6 -mt-6 mb-1 flex items-center gap-2 border-b bg-background px-6 py-3 text-sm font-medium opacity-90 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:hidden"><ChevronLeft className="size-5" />{t("nav.back")}</button>}{children}{showCloseButton && <button type="button" data-slot="dialog-close" onClick={() => ctx.setOpen(false)} className="ring-offset-background focus:ring-ring absolute top-4 right-4 hidden rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none sm:block [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"><XIcon /><span className="sr-only">Close</span></button>}</DialogContainerContext.Provider>;
    $[26] = children;
    $[27] = ctx;
    $[28] = dialogEl;
    $[29] = showCloseButton;
    $[30] = t;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== handleCancel || $[33] !== handleClick || $[34] !== props || $[35] !== t10 || $[36] !== t9) {
    t11 = <dialog ref={setDialogEl} data-slot="dialog-content" onCancel={handleCancel} onClick={handleClick} className={t9} {...props}>{t10}</dialog>;
    $[32] = handleCancel;
    $[33] = handleClick;
    $[34] = props;
    $[35] = t10;
    $[36] = t9;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  return t11;
}

// --- structural parts ----------------------------------------------------------

function DialogHeader(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex flex-col gap-2 text-left", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="dialog-header" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function DialogFooter(t0) {
  const $ = _c(14);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      showCloseButton: t1,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const showCloseButton = t1 === undefined ? false : t1;
  let t2;
  if ($[5] !== className) {
    t2 = cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== showCloseButton) {
    t3 = showCloseButton && <DialogClose asChild={true}><Button variant="outline">Close</Button></DialogClose>;
    $[7] = showCloseButton;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== children || $[10] !== props || $[11] !== t2 || $[12] !== t3) {
    t4 = <div data-slot="dialog-footer" className={t2} {...props}>{children}{t3}</div>;
    $[9] = children;
    $[10] = props;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
function DialogTitle(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-lg leading-none font-semibold", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <h2 data-slot="dialog-title" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function DialogDescription(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-muted-foreground text-sm", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <p data-slot="dialog-description" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger };